// ============================================
// 1. MODELS - event.model.ts
// ============================================

export interface AppEvent {
  id: string;
  userId: string;
  name: string;
  eventDate: Date;
  startTime: string;
  endTime: string;
  duration: number;
  location: string | EventLocation;
  lat?: number;
  lng?: number;
  eventType: EventType;
  expectedAttendance?: AttendanceLevel | number;
  weatherCondition?: WeatherCondition;
  temperature?: number;
  notes?: string;
  recurrence?: 'none' | 'weekly' | 'biweekly' | 'monthly';
  status: EventStatus;
  actualRevenue?: number;
  estimatedRevenue?: { low: number; high: number };
  estimatedMargin?: { low: number; high: number };
  estimatedAttendance?: number; // affluence numérique (opportunités)
  externalId?: string; // ID externe (pour opportunités suivies)
  source?: string; // Source de l'événement ('opportunity', 'manual', etc.)
  createdAt: Date;
  updatedAt: Date;
}

export type EventType =
  | 'market'
  | 'festival'
  | 'private'
  | 'corporate'
  | 'school'
  | 'other';
export type AttendanceLevel = 'low' | 'medium' | 'high' | 'unknown';
export type WeatherCondition =
  | 'sunny'
  | 'cloudy'
  | 'rain_light'
  | 'rain_heavy'
  | 'snow'
  | 'storm';
export type EventStatus =
  | 'planned'
  | 'active'
  | 'completed'
  | 'analyzed'
  | 'cancelled';

// Legacy alias — some backend endpoints still return 'upcoming'
export function normalizeEventStatus(status: string): EventStatus {
  if (status === 'upcoming') return 'planned';
  return status as EventStatus;
}

export interface Recommandation {
  id: string;
  eventId: string;
  productId: string;
  productName: string;
  unit: string;
  recommended: number;
  rangeLow: number;
  rangeHigh: number;
  explanation: string;
  coefficients: {
    base: number;
    eventType: number;
    weather: number;
    duration: number;
  };
}

export interface EventLocation {
  address: string;
  city: string;
  coordinates: { lat: number; lng: number };
  /** Données trajet OSRM sauvegardées en DB */
  routeData?: {
    distanceKm: number;
    durationMin: number;
    geometry: { type: 'LineString'; coordinates: number[][] };
  };
}

export interface CreateEventDto {
  name: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: EventLocation;
  eventType: EventType;
  expectedAttendance?: AttendanceLevel;
  notes?: string;
  recurrence?: 'none' | 'weekly' | 'biweekly' | 'monthly';
}

export const EVENT_TYPES = [
  {
    value: 'market',
    label: 'Marché',
    icon: '🛒',
    description: 'Marché hebdomadaire ou mensuel',
    coef: 1.0,
  },
  {
    value: 'festival',
    label: 'Festival',
    icon: '🎪',
    description: 'Festival musique, culture',
    coef: 1.5,
  },
  {
    value: 'private',
    label: 'Événement privé',
    icon: '🎉',
    description: 'Mariage, anniversaire',
    coef: 0.8,
  },
  {
    value: 'corporate',
    label: 'Événement entreprise',
    icon: '🏢',
    description: 'Séminaire, salon pro',
    coef: 0.9,
  },
  {
    value: 'school',
    label: 'Événement scolaire',
    icon: '🏫',
    description: 'Fête école, kermesse',
    coef: 1.1,
  },
  {
    value: 'other',
    label: 'Autre',
    icon: '📅',
    description: 'Autre type',
    coef: 1.0,
  },
];

export const ATTENDANCE_LEVELS = [
  { value: 'low', label: 'Faible (< 500)', coef: 0.7 },
  { value: 'medium', label: 'Moyenne (500-2000)', coef: 1.0 },
  { value: 'high', label: 'Forte (> 2000)', coef: 1.4 },
  { value: 'unknown', label: 'Inconnue', coef: 1.0 },
];

export const WEATHER_ICONS = {
  sunny: '☀️',
  cloudy: '☁️',
  rain_light: '🌧️',
  rain_heavy: '⛈️',
  snow: '❄️',
  storm: '⛈️',
};
